import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../FirebaseAppContext';
import { useOrganizationId } from '../auth/hooks';
import { DatePickerInput } from '../components/DatePickerInput';
import { FormInput } from '../components/FormInput';
import { useToast } from '../components/ToastProvider';
import './FormPages.css';

type FormData = {
  make: string;
  model: string;
  year: string;
  licensePlate: string;
  licenseExpirationDate: string;
  mileage: string;
  vin: string;
  insuranceProvider: string;
  insuranceExpirationDate: string;
  fitnessExpirationDate: string;
  status: 'available' | 'rented' | 'maintenance';
};

export default function CarForm() {
  const { carId } = useParams<{ carId: string }>();
  const navigate = useNavigate();
  const { state, carOperations } = useAppContext();
  const organizationId = useOrganizationId();
  const isEditMode = carId !== undefined;
  const car = isEditMode ? state.cars.find(c => c.id === carId) : undefined;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const { control, handleSubmit, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      make: car?.make || '',
      model: car?.model || '',
      year: car?.year?.toString() || '',
      licensePlate: car?.licensePlate || '',
      licenseExpirationDate: car?.licenseExpirationDate || '',
      mileage: car?.mileage?.toString() || '0',
      vin: car?.vin || '',
      insuranceProvider: car?.insuranceProvider || '',
      insuranceExpirationDate: car?.insuranceExpirationDate || '',
      fitnessExpirationDate: car?.fitnessExpirationDate || '',
      status: car?.status || 'available',
    },
    mode: 'onChange'
  });

  useEffect(() => {
    if (car) {
      setValue('make', car.make);
      setValue('model', car.model);
      setValue('year', car.year.toString());
      setValue('licensePlate', car.licensePlate);
      setValue('licenseExpirationDate', car.licenseExpirationDate || '');
      setValue('mileage', car.mileage.toString());
      setValue('vin', car.vin || '');
      setValue('insuranceProvider', car.insuranceProvider || '');
      setValue('insuranceExpirationDate', car.insuranceExpirationDate || '');
      setValue('fitnessExpirationDate', car.fitnessExpirationDate || '');
      setValue('status', car.status);
    }
  }, [car, setValue]);

  const onSubmit = async (data: FormData) => {
    if (!organizationId) {
      alert('Organization ID is required');
      return;
    }

    setIsSubmitting(true);
    try {
      const carData = {
        organizationId,
        make: data.make.trim(),
        model: data.model.trim(),
        year: parseInt(data.year, 10),
        licensePlate: data.licensePlate.trim(),
        licenseExpirationDate: data.licenseExpirationDate || undefined,
        mileage: parseInt(data.mileage, 10) || 0,
        vin: data.vin.trim() || undefined,
        insuranceProvider: data.insuranceProvider.trim() || undefined,
        insuranceExpirationDate: data.insuranceExpirationDate || undefined,
        fitnessExpirationDate: data.fitnessExpirationDate || undefined,
        status: data.status,
      };

      if (isEditMode && carId) {
        await carOperations.update(carId, carData);
      } else {
        await carOperations.add(carData);
      }
      showToast({ message: isEditMode ? 'Car updated successfully.' : 'Car added successfully.' });
      navigate('/cars');
    } catch (err) {
      showToast({ message: 'Failed to save car: ' + (err as Error).message, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-page">
      <div className="form-page-header">
        <button type="button" className="btn btn-secondary" onClick={() => navigate('/cars')}>← Back</button>
        <h1 className="form-page-title">{isEditMode ? 'Edit Car' : 'Add New Car'}</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="form-card">
        <div className="form-stack">
          <Controller
            control={control}
            name="make"
            rules={{ required: 'Make is required' }}
            render={({ field }) => (
              <FormInput
                label="Make"
                value={field.value}
                onChange={field.onChange}
                error={errors.make?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="model"
            rules={{ required: 'Model is required' }}
            render={({ field }) => (
              <FormInput
                label="Model"
                value={field.value}
                onChange={field.onChange}
                error={errors.model?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="year"
            rules={{ required: 'Year is required', pattern: { value: /^\d{4}$/, message: 'Year must be 4 digits' } }}
            render={({ field }) => (
              <FormInput
                label="Year"
                type="number"
                value={field.value}
                onChange={field.onChange}
                error={errors.year?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="licensePlate"
            rules={{ required: 'License plate is required' }}
            render={({ field }) => (
              <FormInput
                label="License Plate"
                value={field.value}
                onChange={field.onChange}
                error={errors.licensePlate?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="mileage"
            rules={{ required: 'Mileage is required', pattern: { value: /^\d+$/, message: 'Mileage must be a number' } }}
            render={({ field }) => (
              <FormInput
                label="Mileage (km)"
                type="number"
                value={field.value}
                onChange={field.onChange}
                error={errors.mileage?.message}
                required
              />
            )}
          />

          <Controller
            control={control}
            name="status"
            render={({ field }) => (
              <div className="form-field">
                <label className="form-field-label">
                  Status
                </label>
                <select className="form-select" value={field.value} onChange={(e) => field.onChange(e.target.value)}>
                  <option value="available">Available</option>
                  <option value="rented">Rented</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            )}
          />

          <Controller
            control={control}
            name="vin"
            render={({ field }) => (
              <FormInput
                label="VIN (optional)"
                value={field.value}
                onChange={field.onChange}
                error={errors.vin?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="fitnessExpirationDate"
            render={({ field }) => (
              <DatePickerInput
                label="Fitness Expiration Date (optional)"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            control={control}
            name="licenseExpirationDate"
            render={({ field }) => (
              <DatePickerInput
                label="License Expiration Date (optional)"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            control={control}
            name="insuranceProvider"
            render={({ field }) => (
              <FormInput
                label="Insurance Provider (optional)"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <Controller
            control={control}
            name="insuranceExpirationDate"
            render={({ field }) => (
              <DatePickerInput
                label="Insurance Expiration Date (optional)"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />

          <div className="form-actions">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary"
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update Car' : 'Add Car'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/cars')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

